import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useConfirmDialog } from './ConfirmDialog';
import api from '../utils/api';
import { getPublicAuditionShareUrl, shareLink } from '../utils/share';
import './GeneralAuditionsPanel.css';

type ManagedAudition = {
  id: number;
  story_id: number | null;
  storyline: string;
  character_line: string;
  description: string;
  is_published: boolean;
  created_at?: string;
  updated_at?: string;
  audition_scope?: 'story' | 'general';
  story_title?: string | null;
  owner_name?: string | null;
  owner_email?: string | null;
};

type AuditionMedia = {
  id: number;
  url: string;
  media_type: 'photo' | 'video' | string;
  file_name?: string;
};

type AuditionResponse = {
  id: number;
  user_id: number;
  name: string;
  email: string;
  file_name?: string;
  url?: string | null;
  media_type?: string;
  created_at?: string;
};

type MediaPreview = {
  url: string;
  type: 'photo' | 'video' | 'pdf';
  mediaId?: number;
  responseId?: number;
};

const emptyPreview: MediaPreview | null = null;

const isPdfAsset = (mediaType?: string, fileName?: string | null, url?: string | null) => {
  const target = `${fileName || ''} ${url || ''}`.toLowerCase();
  return mediaType === 'pdf' || target.includes('.pdf');
};

const getPreviewType = (mediaType?: string, fileName?: string | null, url?: string | null): MediaPreview['type'] => {
  if (isPdfAsset(mediaType, fileName, url)) return 'pdf';
  return mediaType === 'photo' ? 'photo' : 'video';
};

const downloadBlob = (blob: Blob, fileName?: string) => {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName || 'submission';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
};

const getDownloadFileName = (headers: any, fallback?: string) => {
  const disposition = String(headers?.['content-disposition'] || '');
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match?.[1] ? decodeURIComponent(match[1]) : fallback || 'submission';
};

interface GeneralAuditionsPanelProps {
  onBrowseAuditions?: () => void;
}

export function GeneralAuditionsPanel({ onBrowseAuditions }: GeneralAuditionsPanelProps) {
  const navigate = useNavigate();
  const { confirm, confirmDialog } = useConfirmDialog();
  const [auditions, setAuditions] = useState<ManagedAudition[]>([]);
  const [selectedAuditionId, setSelectedAuditionId] = useState<number | null>(null);
  const [draftId, setDraftId] = useState<number | null>(null);
  const [currentAudition, setCurrentAudition] = useState<ManagedAudition | null>(null);
  const [mediaItems, setMediaItems] = useState<AuditionMedia[]>([]);
  const [responses, setResponses] = useState<AuditionResponse[]>([]);
  const [role, setRole] = useState('');
  const [description, setDescription] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [connectedIds, setConnectedIds] = useState<Set<number>>(new Set());
  const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());
  const [preview, setPreview] = useState<MediaPreview | null>(emptyPreview);

  const messageTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftPromiseRef = useRef<Promise<number | null> | null>(null);
  const refreshingMediaRef = useRef<Set<number>>(new Set());
  const refreshingResponseRef = useRef<Set<number>>(new Set());

  const isExistingAudition = selectedAuditionId !== null;
  const isDraftAudition = draftId !== null && selectedAuditionId === null;
  const activeAuditionId = selectedAuditionId ?? draftId;
  const fieldsDisabled = isExistingAudition && !isEditMode;
  const managedAuditions = auditions.filter((audition) => audition.id !== draftId);
  const isViewingPublishedAudition = Boolean(
    selectedAuditionId !== null &&
      currentAudition?.id === selectedAuditionId &&
      currentAudition?.is_published,
  );
  const isViewingClosedAudition = Boolean(
    selectedAuditionId !== null &&
      currentAudition?.id === selectedAuditionId &&
      !currentAudition?.is_published,
  );

  const refreshMediaUrl = useCallback(async (mediaId?: number) => {
    if (!mediaId || !activeAuditionId || refreshingMediaRef.current.has(mediaId)) return null;
    refreshingMediaRef.current.add(mediaId);
    try {
      const response = await api.get(`/public-auditions/${activeAuditionId}/media`);
      const nextMedia: AuditionMedia[] = response.data.media || [];
      setMediaItems(nextMedia);
      const freshItem = nextMedia.find((item) => Number(item.id) === Number(mediaId));
      const freshUrl = freshItem?.url || null;
      if (freshUrl) {
        setPreview((current) => (current?.mediaId === mediaId ? { ...current, url: freshUrl } : current));
      }
      return freshUrl;
    } catch (refreshError) {
      console.error('Refresh audition media URL failed:', refreshError);
      return null;
    } finally {
      refreshingMediaRef.current.delete(mediaId);
    }
  }, [activeAuditionId]);

  const refreshResponseUrl = useCallback(async (responseId?: number) => {
    if (!responseId || refreshingResponseRef.current.has(responseId)) return null;
    refreshingResponseRef.current.add(responseId);
    try {
      const response = await api.get(`/public-auditions/${responseId}/response-media`);
      const freshUrl = response.data?.url || null;
      if (freshUrl) {
        setResponses((current) =>
          current.map((item) => (Number(item.id) === Number(responseId) ? { ...item, url: freshUrl } : item)),
        );
        setPreview((current) => (current?.responseId === responseId ? { ...current, url: freshUrl } : current));
      }
      return freshUrl;
    } catch (refreshError) {
      console.error('Refresh audition response URL failed:', refreshError);
      return null;
    } finally {
      refreshingResponseRef.current.delete(responseId);
    }
  }, []);

  const handlePreviewError = useCallback(() => {
    if (preview?.responseId) {
      void refreshResponseUrl(preview.responseId);
    } else if (preview?.mediaId) {
      void refreshMediaUrl(preview.mediaId);
    }
  }, [preview?.mediaId, preview?.responseId, refreshMediaUrl, refreshResponseUrl]);

  const flashMessage = useCallback((value: string) => {
    setMessage(value);
    setError('');
    if (messageTimerRef.current) clearTimeout(messageTimerRef.current);
    messageTimerRef.current = setTimeout(() => setMessage(''), 3000);
  }, []);

  const flashError = useCallback((value: string) => {
    setError(value);
    setMessage('');
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => setError(''), 3000);
  }, []);

  useEffect(() => () => {
    if (messageTimerRef.current) clearTimeout(messageTimerRef.current);
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
  }, []);

  const resetComposer = useCallback(() => {
    setSelectedAuditionId(null);
    setDraftId(null);
    setCurrentAudition(null);
    setMediaItems([]);
    setResponses([]);
    setRole('');
    setDescription('');
    setIsEditMode(false);
  }, []);

  const loadConnectionState = useCallback(async () => {
    try {
      const [connectionsRes, sentRes] = await Promise.all([
        api.get('/connections'),
        api.get('/connections/requests/sent'),
      ]);

      setConnectedIds(
        new Set(
          (connectionsRes.data.connections || []).map((connection: { user_id: number }) => Number(connection.user_id)),
        ),
      );
      setPendingIds(
        new Set(
          (sentRes.data.requests || [])
            .filter((request: { status?: string }) => request.status === 'pending')
            .map((request: { other_user_id: number }) => Number(request.other_user_id)),
        ),
      );
    } catch (loadError) {
      console.error('Failed to load connection state:', loadError);
    }
  }, []);

  const loadOwnedAuditions = useCallback(async () => {
    const response = await api.get('/public-auditions/general/mine');
    const nextAuditions: ManagedAudition[] = response.data.auditions || [];
    setAuditions(nextAuditions);
    return nextAuditions;
  }, []);

  const loadAuditionDetail = useCallback(
    async (auditionId: number, mode: 'published' | 'draft' = 'published') => {
      setDetailLoading(true);
      try {
        const response = await api.get(`/public-auditions/${auditionId}`);
        const audition: ManagedAudition = response.data.audition;
        setCurrentAudition(audition);
        setMediaItems(response.data.media || []);
        setResponses(response.data.responses || []);
        setRole(audition.character_line || '');
        setDescription(audition.description || '');
        setIsEditMode(false);

        if (mode === 'published') {
          setSelectedAuditionId(auditionId);
          setDraftId(null);
        } else {
          setDraftId(auditionId);
          setSelectedAuditionId(null);
        }
      } catch (loadError: any) {
        flashError(loadError?.response?.data?.message || 'Failed to load audition');
      } finally {
        setDetailLoading(false);
      }
    },
    [flashError],
  );

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([loadOwnedAuditions(), loadConnectionState()]).finally(() => setLoading(false));
  }, [loadConnectionState, loadOwnedAuditions]);

  const ensureDraftExists = useCallback(async () => {
    if (selectedAuditionId) return selectedAuditionId;
    if (draftId) return draftId;
    if (draftPromiseRef.current) return draftPromiseRef.current;

    draftPromiseRef.current = api
      .post('/public-auditions/general/draft')
      .then((response) => {
        const createdId = Number(response.data.audition.id);
        setDraftId(createdId);
        return createdId;
      })
      .catch((draftError: any) => {
        flashError(draftError?.response?.data?.message || 'Failed to start a new audition draft');
        return null;
      })
      .finally(() => {
        draftPromiseRef.current = null;
      });

    return draftPromiseRef.current;
  }, [draftId, flashError, selectedAuditionId]);

  const handleSelectAudition = useCallback(
    (value: string) => {
      if (value === 'new') {
        resetComposer();
        return;
      }
      void loadAuditionDetail(Number(value), 'published');
    },
    [loadAuditionDetail, resetComposer],
  );

  const handleRoleChange = useCallback(
    (value: string) => {
      setRole(value);
      if (!isExistingAudition) void ensureDraftExists();
    },
    [ensureDraftExists, isExistingAudition],
  );

  const handleDescriptionChange = useCallback(
    (value: string) => {
      setDescription(value);
      if (!isExistingAudition) void ensureDraftExists();
    },
    [ensureDraftExists, isExistingAudition],
  );

  const refreshAfterSave = useCallback(
    async (auditionId: number) => {
      await loadOwnedAuditions();
      await loadAuditionDetail(auditionId, 'published');
    },
    [loadAuditionDetail, loadOwnedAuditions],
  );

  const handlePublish = useCallback(async () => {
    if (!role.trim() || !description.trim()) {
      flashError('Role and description are required');
      return;
    }

    setSaving(true);
    try {
      let saved: ManagedAudition;
      const pendingDraftId = draftPromiseRef.current ? await draftPromiseRef.current : draftId;

      if (pendingDraftId) {
        const response = await api.patch(`/public-auditions/${pendingDraftId}/publish`, {
          storyline: '',
          character_line: role.trim(),
          description: description.trim(),
        });
        saved = response.data.audition;
      } else {
        const response = await api.post('/public-auditions/general', {
          storyline: '',
          character_line: role.trim(),
          description: description.trim(),
        });
        saved = response.data.audition;
      }

      flashMessage('General audition published');
      await refreshAfterSave(saved.id);
    } catch (saveError: any) {
      flashError(saveError?.response?.data?.message || 'Failed to publish audition');
    } finally {
      setSaving(false);
    }
  }, [description, draftId, flashError, flashMessage, refreshAfterSave, role]);

  const handleEnterEditMode = useCallback(() => {
    if (!currentAudition) return;
    setRole(currentAudition.character_line || '');
    setDescription(currentAudition.description || '');
    setIsEditMode(true);
  }, [currentAudition]);

  const handleCancelEdit = useCallback(() => {
    setRole(currentAudition?.character_line || '');
    setDescription(currentAudition?.description || '');
    setIsEditMode(false);
  }, [currentAudition]);

  const handleSaveEdit = useCallback(async () => {
    if (!selectedAuditionId) return;
    if (!role.trim() || !description.trim()) {
      flashError('Role and description are required');
      return;
    }

    setSaving(true);
    try {
      await api.patch(`/public-auditions/${selectedAuditionId}`, {
        storyline: '',
        character_line: role.trim(),
        description: description.trim(),
      });
      flashMessage('General audition updated');
      await refreshAfterSave(selectedAuditionId);
    } catch (saveError: any) {
      flashError(saveError?.response?.data?.message || 'Failed to update audition');
    } finally {
      setSaving(false);
    }
  }, [description, flashError, flashMessage, refreshAfterSave, role, selectedAuditionId]);

  const handleCloseAudition = useCallback(async () => {
    if (!selectedAuditionId) return;
    const approved = await confirm({
      title: 'Close Audition',
      message: 'Close this audition? It will disappear from Open Auditions, but stay here so you can review submissions.',
      confirmLabel: 'Close Audition',
      cancelLabel: 'Keep Open',
      danger: true,
    });
    if (!approved) return;

    try {
      await api.patch(`/public-auditions/${selectedAuditionId}/close`);
      flashMessage('General audition closed');
      await loadOwnedAuditions();
      await loadAuditionDetail(selectedAuditionId, 'published');
    } catch (closeError: any) {
      flashError(closeError?.response?.data?.message || 'Failed to close audition');
    }
  }, [confirm, flashError, flashMessage, loadAuditionDetail, loadOwnedAuditions, selectedAuditionId]);

  const handleRemoveAudition = useCallback(async () => {
    if (!activeAuditionId) return;
    const approved = await confirm({
      title: 'Remove Audition',
      message: 'Remove this audition permanently? This will delete the audition and all of its submissions.',
      confirmLabel: 'Remove Audition',
      cancelLabel: 'Keep Audition',
      danger: true,
    });
    if (!approved) return;

    try {
      await api.delete(`/public-auditions/${activeAuditionId}`);
      flashMessage('General audition removed');
      resetComposer();
      await loadOwnedAuditions();
    } catch (removeError: any) {
      flashError(removeError?.response?.data?.message || 'Failed to remove audition');
    }
  }, [activeAuditionId, confirm, flashError, flashMessage, loadOwnedAuditions, resetComposer]);

  const handleMediaUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);
      if (files.length === 0) return;
      const oversized = files.find((file) => file.size > 100 * 1024 * 1024);
      if (oversized) {
        flashError(`"${oversized.name}" exceeds the 100MB limit`);
        event.target.value = '';
        return;
      }

      setUploadingMedia(true);
      try {
        const targetId = activeAuditionId ?? (await ensureDraftExists());
        if (!targetId) {
          event.target.value = '';
          return;
        }

        for (const file of files) {
          const formData = new FormData();
          formData.append('file', file);

          await api.post(`/public-auditions/${targetId}/media`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }

        flashMessage(files.length === 1 ? 'Media uploaded' : `${files.length} media items uploaded`);
        await loadAuditionDetail(targetId, selectedAuditionId ? 'published' : 'draft');
      } catch (uploadError: any) {
        flashError(uploadError?.response?.data?.message || 'Failed to upload media');
      } finally {
        setUploadingMedia(false);
        event.target.value = '';
      }
    },
    [activeAuditionId, ensureDraftExists, flashError, flashMessage, loadAuditionDetail, selectedAuditionId],
  );

  const handleDeleteMedia = useCallback(
    async (mediaId: number) => {
      if (!activeAuditionId) return;
      const approved = await confirm({
        title: 'Delete Media',
        message: 'Delete this media item from the audition?',
        confirmLabel: 'Delete Media',
        cancelLabel: 'Keep Media',
        danger: true,
      });
      if (!approved) return;

      try {
        await api.delete(`/public-auditions/${activeAuditionId}/media/${mediaId}`);
        flashMessage('Media deleted');
        await loadAuditionDetail(activeAuditionId, selectedAuditionId ? 'published' : 'draft');
      } catch (deleteError: any) {
        flashError(deleteError?.response?.data?.message || 'Failed to delete media');
      }
    },
    [activeAuditionId, confirm, flashError, flashMessage, loadAuditionDetail, selectedAuditionId],
  );

  const handleConnect = useCallback(
    async (targetUserId: number) => {
      try {
        await api.post('/connections/request', { targetUserId });
        setPendingIds((prev) => new Set([...prev, targetUserId]));
        flashMessage('Connection request sent');
      } catch (connectError: any) {
        flashError(connectError?.response?.data?.message || 'Failed to send connection request');
      }
    },
    [flashError, flashMessage],
  );

  const selectedOptionValue = selectedAuditionId == null ? 'new' : String(selectedAuditionId);

  const responseActionMeta = useCallback(
    (userId: number) => {
      if (connectedIds.has(userId)) {
        return {
          label: 'Chat',
          disabled: false,
          action: () => navigate(`/direct-messages/${userId}`),
        };
      }

      if (pendingIds.has(userId)) {
        return {
          label: 'Pending',
          disabled: true,
          action: () => undefined,
        };
      }

      return {
        label: 'Connect',
        disabled: false,
        action: () => void handleConnect(userId),
      };
    },
    [connectedIds, handleConnect, navigate, pendingIds],
  );

  const handleDownloadResponse = useCallback(
    async (response: AuditionResponse) => {
      try {
        const res = await api.get(`/public-auditions/${response.id}/response-media?download=1`, {
          responseType: 'blob',
        });
        downloadBlob(res.data, getDownloadFileName(res.headers, response.file_name || `${response.name}-submission`));
      } catch (downloadError: any) {
        flashError(downloadError?.response?.data?.message || 'Failed to download submission');
      }
    },
    [flashError],
  );

  const handleDownloadAllResponses = useCallback(async () => {
    const downloadableResponses = responses.filter((response) => response.id);
    if (downloadableResponses.length === 0) return;

    try {
      for (const response of downloadableResponses) {
        const res = await api.get(`/public-auditions/${response.id}/response-media?download=1`, {
          responseType: 'blob',
        });
        downloadBlob(res.data, getDownloadFileName(res.headers, response.file_name || `${response.name}-submission`));
      }
      flashMessage('All submissions downloaded');
    } catch (downloadError: any) {
      flashError(downloadError?.response?.data?.message || 'Failed to download all submissions');
    }
  }, [flashError, flashMessage, responses]);

  const handleBrowseAuditions = useCallback(() => {
    if (onBrowseAuditions) {
      onBrowseAuditions();
      return;
    }
    navigate('/open-auditions');
  }, [navigate, onBrowseAuditions]);

  const handleShareAudition = useCallback(async () => {
    if (!selectedAuditionId || !currentAudition?.is_published) return;

    try {
      const outcome = await shareLink(
        getPublicAuditionShareUrl(selectedAuditionId),
        `${currentAudition.character_line || `Audition #${selectedAuditionId}`} on FilMini`,
      );
      if (outcome === 'copied') {
        flashMessage('Audition link copied');
      }
    } catch (shareError) {
      console.error('Failed to share audition:', shareError);
      flashError('Failed to share audition link');
    }
  }, [currentAudition, flashError, flashMessage, selectedAuditionId]);

  const dropdownOptions = useMemo(
    () =>
      managedAuditions.map((audition) => ({
        value: String(audition.id),
        label: `${audition.is_published ? 'Open' : 'Closed'} - ${
          audition.character_line?.trim() ||
          audition.description?.slice(0, 48) ||
          `General Audition #${audition.id}`
        }`,
      })),
    [managedAuditions],
  );

  return (
    <>
      {confirmDialog}
      <section className="general-auditions-section">
        <div className="general-auditions-headbar general-auditions-header">
          <div>
            <h2>Public General Auditions</h2>
            <p className="general-auditions-subtitle">
              Host open casting calls in one place without creating a story group first.
            </p>
          </div>
          <button onClick={handleBrowseAuditions} className="general-auditions-secondary-btn">
            Browse Public Auditions
          </button>
        </div>

        {loading ? (
          <div className="general-auditions-loading">Loading auditions...</div>
        ) : (
          <>
            {message ? <div className="general-auditions-banner">{message}</div> : null}
            {error ? <div className="general-auditions-banner general-auditions-banner--error">{error}</div> : null}

            <div className="general-auditions-toolbar">
              <div className="general-auditions-selector">
                <label htmlFor="general-audition-selector">Your General Auditions</label>
                {dropdownOptions.length > 0 ? (
                  <select
                    id="general-audition-selector"
                    className="general-auditions-dropdown"
                    value={selectedOptionValue}
                    onChange={(event) => handleSelectAudition(event.target.value)}
                    disabled={detailLoading}
                  >
                    <option value="new">- New Audition -</option>
                    {dropdownOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="general-auditions-empty">No general auditions yet</span>
                )}
              </div>

              {isExistingAudition || isDraftAudition ? (
                <button
                  type="button"
                  className="general-auditions-secondary-btn"
                  onClick={resetComposer}
                >
                  + New Audition
                </button>
              ) : null}
            </div>

            <div className="general-auditions-form">
              <div className="general-auditions-form-header">
                <div>
                  <h3>
                    {isExistingAudition
                      ? isEditMode
                        ? 'Edit General Audition'
                        : isViewingClosedAudition
                          ? 'Closed General Audition'
                          : 'Published General Audition'
                      : isDraftAudition
                        ? 'New General Audition (Draft)'
                        : 'New General Audition'}
                  </h3>
                  <p>
                    {isViewingClosedAudition && !isEditMode
                      ? 'This audition is closed. It stays here so you can still review every submission.'
                      : isExistingAudition && !isEditMode
                      ? 'This audition is live on the Open Auditions page.'
                      : isExistingAudition && isEditMode
                        ? isViewingClosedAudition
                          ? 'Update the role or description, then save your changes. This audition remains closed.'
                          : 'Update the role or description, then save your changes.'
                        : 'Set the role, add the description, and publish when ready.'}
                  </p>
                </div>

                <div className="general-auditions-form-actions">
                  {isViewingPublishedAudition && !isEditMode ? <span className="general-auditions-pill">Published</span> : null}
                  {isViewingClosedAudition && !isEditMode ? (
                    <span className="general-auditions-pill general-auditions-pill--draft">Closed</span>
                  ) : null}
                  {isDraftAudition ? <span className="general-auditions-pill general-auditions-pill--draft">Draft</span> : null}

                  {(isViewingPublishedAudition || isViewingClosedAudition) && !isEditMode ? (
                    <div className="general-auditions-owner-actions">
                      {isViewingPublishedAudition ? (
                        <button type="button" className="general-auditions-secondary-btn" onClick={() => void handleShareAudition()}>
                          Share
                        </button>
                      ) : null}
                      <button type="button" className="general-auditions-secondary-btn" onClick={handleEnterEditMode}>
                        Edit
                      </button>
                      {isViewingPublishedAudition ? (
                        <button type="button" className="general-auditions-danger-btn" onClick={handleCloseAudition}>
                          Close Audition
                        </button>
                      ) : null}
                      <button type="button" className="general-auditions-danger-btn" onClick={handleRemoveAudition}>
                        Remove Audition
                      </button>
                    </div>
                  ) : null}

                  {isDraftAudition ? (
                    <div className="general-auditions-owner-actions">
                      <button type="button" className="general-auditions-danger-btn" onClick={handleRemoveAudition}>
                        Remove Audition
                      </button>
                    </div>
                  ) : null}

                  {isExistingAudition && isEditMode ? (
                    <div className="general-auditions-owner-actions">
                      <button
                        type="button"
                        className="general-auditions-primary-btn"
                        onClick={handleSaveEdit}
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button
                        type="button"
                        className="general-auditions-secondary-btn"
                        onClick={handleCancelEdit}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>

              {detailLoading ? <div className="general-auditions-loading">Loading audition...</div> : null}

              <div className="general-auditions-field">
                <label htmlFor="general-audition-role">
                  Role <span>*</span>
                </label>
                <input
                  id="general-audition-role"
                  type="text"
                  value={role}
                  onChange={(event) => handleRoleChange(event.target.value)}
                  placeholder="Lead actor, editor, cinematographer..."
                  className="general-auditions-input"
                  disabled={fieldsDisabled}
                />
              </div>

              <div className="general-auditions-field">
                <label htmlFor="general-audition-description">
                  Description <span>*</span>
                </label>
                <textarea
                  id="general-audition-description"
                  value={description}
                  onChange={(event) => handleDescriptionChange(event.target.value)}
                  placeholder="What are you looking for, what should applicants know, and what kind of submission fits best?"
                  className="general-auditions-textarea"
                  rows={6}
                  disabled={fieldsDisabled}
                />
              </div>

              <div className="general-auditions-media-block">
                <div className="general-auditions-media-header">
                  <h4>Media</h4>
                  <p>Add reference photos or videos for applicants if needed.</p>
                </div>

                {mediaItems.length > 0 ? (
                  <div className="general-auditions-media-grid">
                    {mediaItems.map((item) => (
                      <div key={item.id} className="general-auditions-media-card">
                        <div
                          className="general-auditions-media-preview"
                          onClick={() =>
                            setPreview({
                              url: item.url,
                              type: item.media_type === 'photo' ? 'photo' : 'video',
                              mediaId: item.id,
                            })
                          }
                        >
                          {item.media_type === 'photo' ? (
                            <img src={item.url} alt="" onError={() => { void refreshMediaUrl(item.id); }} />
                          ) : (
                            <video
                              preload="metadata"
                              muted
                              src={`${item.url}#t=0.5`}
                              onError={() => { void refreshMediaUrl(item.id); }}
                            >
                            </video>
                          )}
                        </div>
                        <button
                          type="button"
                          className="general-auditions-danger-btn general-auditions-danger-btn--small"
                          onClick={() => void handleDeleteMedia(item.id)}
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="general-auditions-empty-state">No media attached yet.</div>
                )}

                <div className="general-auditions-upload">
                  <input
                    id="general-audition-media-upload"
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={(event) => void handleMediaUpload(event)}
                    disabled={uploadingMedia}
                  />
                  <label htmlFor="general-audition-media-upload" className="general-auditions-secondary-btn">
                    {uploadingMedia ? 'Uploading...' : 'Upload Media'}
                  </label>
                </div>
              </div>

              {!isExistingAudition ? (
                <div className="general-auditions-save-row">
                  <button
                    type="button"
                    className="general-auditions-primary-btn"
                    onClick={handlePublish}
                    disabled={saving}
                  >
                    {saving ? 'Publishing...' : 'Save & Publish'}
                  </button>
                </div>
              ) : null}
            </div>

            {isExistingAudition ? (
              <div className="general-auditions-responses">
                <div className="general-auditions-responses-head">
                  <div>
                    <h3>Submissions ({responses.length})</h3>
                    <p>Review the submission and decide whether to connect or start chatting.</p>
                  </div>
                  {responses.length > 0 ? (
                    <button
                      type="button"
                      className="general-auditions-secondary-btn"
                      onClick={() => void handleDownloadAllResponses()}
                    >
                      Download All
                    </button>
                  ) : null}
                </div>

                {responses.length === 0 ? (
                  <div className="general-auditions-empty-state">No submissions yet.</div>
                ) : (
                  <div className="general-auditions-responses-grid">
                    {responses.map((response) => {
                      const action = responseActionMeta(response.user_id);
                      return (
                        <div key={response.id} className="general-auditions-response-card">
                          <div className="general-auditions-response-copy">
                            <strong>{response.name}</strong>
                            <span>{response.email}</span>
                          </div>

                          {response.url ? (
                            <div
                              className="general-auditions-response-preview"
                              onClick={() =>
                                setPreview({
                                  url: response.url || '',
                                  type: getPreviewType(response.media_type, response.file_name, response.url),
                                  responseId: response.id,
                                })
                              }
                            >
                              {response.media_type === 'photo' ? (
                                <img src={response.url} alt="" onError={() => { void refreshResponseUrl(response.id); }} />
                              ) : isPdfAsset(response.media_type, response.file_name, response.url) ? (
                                <div className="general-auditions-pdf-preview">
                                  <span>PDF</span>
                                  <small>Open Preview</small>
                                </div>
                              ) : (
                                <video
                                  preload="metadata"
                                  muted
                                  src={`${response.url}#t=0.5`}
                                  onError={() => { void refreshResponseUrl(response.id); }}
                                >
                                </video>
                              )}
                            </div>
                          ) : null}

                          <p className="general-auditions-response-date">
                            Submitted {new Date(response.created_at || Date.now()).toLocaleDateString()}
                          </p>

                          <div className="general-auditions-response-actions">
                            <button
                              type="button"
                              className="general-auditions-primary-btn"
                              onClick={action.action}
                              disabled={action.disabled}
                            >
                              {action.label}
                            </button>
                            <button
                              type="button"
                              className="general-auditions-secondary-btn"
                              onClick={() => void handleDownloadResponse(response)}
                            >
                              Download
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : null}
          </>
        )}
      </section>

      {preview ? (
        <div className="general-auditions-preview-overlay" onClick={() => setPreview(null)}>
          <div className="general-auditions-preview-modal" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="general-auditions-preview-close"
              onClick={() => setPreview(null)}
            >
              Close
            </button>
            {preview.type === 'photo' ? (
              <img src={preview.url} alt="" className="general-auditions-preview-asset" onError={handlePreviewError} />
            ) : preview.type === 'pdf' ? (
              <iframe src={preview.url} title="PDF Preview" className="general-auditions-preview-asset general-auditions-preview-pdf" onError={handlePreviewError} />
            ) : (
              <video controls autoPlay className="general-auditions-preview-asset" src={preview.url} onError={handlePreviewError}>
              </video>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}

export default GeneralAuditionsPanel;
